import { useCallback, useEffect, useRef, useState } from "react";

import { useSelector } from "react-redux";

import { getActiveRole, getRefreshIntervalMs } from "selectors";

import {
  TabInfo,
  TabMessage,
  TabMessageType,
  createTabChannel,
  getTabId,
  getTabOpenedAt,
} from "lib/tabMessaging";
import { getLivestockSaleId } from "lib/navigation";

/** How long to wait for PONG replies after a PING. */
const PONG_COLLECTION_WAIT_MS = 600;

interface UseMultipleTabDetectionResult {
  otherTabs: TabInfo[];
  checkNow: () => void;
}

const isBroadcastChannelSupported = typeof BroadcastChannel !== "undefined";

export const useMultipleTabDetection = (): UseMultipleTabDetectionResult => {
  const activeRole = useSelector(getActiveRole);
  const activeRoleRef = useRef(activeRole);
  activeRoleRef.current = activeRole;

  const tabId = useRef(getTabId());
  const channelRef = useRef<BroadcastChannel | null>(null);
  const pongCollectorRef = useRef<TabInfo[]>([]);
  const pongTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [otherTabs, setOtherTabs] = useState<TabInfo[]>([]);

  // Mirror the heartbeat refresh interval so we check at the same cadence.
  const refreshIntervalMs = useSelector(getRefreshIntervalMs);

  const checkNow = useCallback(() => {
    if (!channelRef.current) return;
    pongCollectorRef.current = [];
    if (pongTimerRef.current) clearTimeout(pongTimerRef.current);

    channelRef.current.postMessage({
      type: TabMessageType.PING,
      fromTabId: tabId.current,
    } satisfies TabMessage);

    pongTimerRef.current = setTimeout(() => {
      const tabs = [...pongCollectorRef.current];
      if (tabs.length > 0) {
        // eslint-disable-next-line no-console
        console.warn(`Warning: Found ${tabs.length} tabs open.`, tabs);
      }
      setOtherTabs(tabs);
    }, PONG_COLLECTION_WAIT_MS);
  }, []);

  // Re-create the periodic check whenever the refresh interval changes.
  useEffect(() => {
    if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    if (refreshIntervalMs) {
      checkIntervalRef.current = setInterval(checkNow, refreshIntervalMs);
    }
    return () => {
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    };
  }, [refreshIntervalMs, checkNow]);

  // Set up the BroadcastChannel once on mount.
  useEffect(() => {
    if (!isBroadcastChannelSupported) return;

    const channel = createTabChannel();
    channelRef.current = channel;

    channel.onmessage = (event: MessageEvent<TabMessage>) => {
      const { data } = event;

      switch (data.type) {
        case TabMessageType.PING:
          if (data.fromTabId !== tabId.current) {
            channel.postMessage({
              type: TabMessageType.PONG,
              fromTabId: tabId.current,
              url: window.location.href,
              path: window.location.pathname + window.location.search,
              openedAt: getTabOpenedAt(),
              livestockSaleId: getLivestockSaleId() || null,
              activeRole: activeRoleRef.current.syncing
                ? null
                : activeRoleRef.current,
            } satisfies TabMessage);
          }
          break;

        case TabMessageType.PONG:
          if (data.fromTabId !== tabId.current) {
            pongCollectorRef.current.push({
              id: data.fromTabId,
              url: data.url,
              path: data.path,
              openedAt: data.openedAt,
              livestockSaleId: data.livestockSaleId,
              activeRole: data.activeRole,
            });
          }
          break;
      }
    };

    // Initial check after a short delay to let other tabs settle.
    const initialTimer = setTimeout(checkNow, 2000);

    return () => {
      clearTimeout(initialTimer);
      if (pongTimerRef.current) clearTimeout(pongTimerRef.current);
      channel.close();
      channelRef.current = null;
    };
  }, [checkNow]);

  return { otherTabs, checkNow };
};
